import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type KeyboardEvent, type MouseEvent as ReactMouseEvent, type RefObject } from 'react'
import {
  aiAsk,
  aiCancel,
  aiConversations,
  aiDevices,
  aiDismissRun,
  aiHistory,
  aiModesQuery,
  aiOpenConversation,
  aiPending,
  aiReset,
  aiRunning,
  aiSession,
  aiSessionCommands,
  aiStream,
  aiTiersQuery,
  aiToTicket,
  aiTranscript,
  type AiConversation,
  type AiDevice,
  type AiMessage,
  type AiRun,
  type AiScope,
  type AiSessionCommand,
  type AiStep,
  type AiStreamEvent,
} from '../api/assistant'
import { setAgentMode, type AgentMode } from '../api/changes'
import { apiGet } from '../api/client'
import type { BellPanel, BellRow } from '../api/notifications'
import { Adam } from '../ui/Adam'
import { toast } from '../ui/notify'
import { renderRich } from './richText'
import { SettingsCheck } from '../ui/SettingsParts'
import { LiveCommands, type LiveStep } from './LiveCommands'
import { AdamSpend } from './AdamSpend'
import { AdamLives, BUBBLE_LINGER, pushTrail, shortTitle, type AdamLiveBubble } from './AdamLives'

/**
 * Sertarul lui Adam - `_partials/od_ai_drawer.php` + `public/js/od_ai.js`,
 * cu aceleasi clase (foaia e `public/css/od_ai.css`, in PORTAL_STYLES).
 *
 * Ce tine: perch-ul din coltul barii cu insigna "cate raspunsuri n-ai vazut",
 * antetul (nume, treapta, Fixed/Dynamic, modul), bara scopului (All / Device,
 * alegerea masinii, History, + New), banda rularilor de pe toate
 * conversatiile, firul cu bulele si pasii, bula de asteptare cu Stop,
 * campul de scris, si dedesubt faptele (save / copy / ticket / schedule) si
 * legaturile Changes / Direct Commands ale discutiei de acum.
 *
 * Comutarea modului e cea din portal: meniul de sub insigna arata numai
 * treptele pe care le are omul (`assistant/modes`), Incident cere un
 * `confirm()`, Full Access trece prin poarta cu riscurile, invoirea si codul
 * pe email (`changes/mode`, cu `need_code`). Blocul MCP se desface de sub
 * "MCP" ca in macheta.
 *
 * Vorbele lui Adam cand nu e chemat (`od-ai-say`) sunt aduse ca in od_ai.js:
 * la 1-5 minute, cat panoul e coborat si fila e la vedere, o bula deasupra
 * capului pentru 10-20 de secunde - o vorba de-a lui, ce e nou in portal (o
 * data pe zi, admin) sau ce e la clopotel (o data pe instiintare, niciodata
 * doua la rand). Pasul si ce s-a spus stau in localStorage, cu ACELEASI chei
 * ca in portal, ca cele doua sa nu-si vorbeasca unul peste altul.
 *
 * STAREA E A SERVERULUI. Istoria vine din `history`, ce a ramas de la
 * intrebarea trecuta din `pending` (`last_assistant_id`, tinut pe browser in
 * localStorage, ca in portal), iar conversatia aleasa din istoric pleaca ca
 * `conversation_id` cu fiecare cerere - v4 n-are sesiune care s-o tina minte.
 */

const SEEN_KEY = 'od_ai_seen_id'
/* Aceleasi chei si valori ca in od_ai.js (`odAiFollow` = `static`, `odAiTier:<user_id>`):
   portalul si v4 stau pe acelasi browser, si alegerea omului e una singura. */
const FOLLOW_KEY = 'odAiFollow'
const tierKey = (user: number) => `odAiTier:${user}`
const UP_KEY = 'od_ai_up'
/*
 * Panoul MARIT peste portal - `od-ai-wide`, nu "full".
 *
 * `full` e luat in fisierul asta, si e modul de acces al agentului ("Full
 * Access", `mode === 'full'`, poarta cu codul pe email). O stare de MARIME
 * numita `isFull` langa el ar fi o incurcatura care asteapta cititorul
 * urmator. Nici `expanded` nu e liber: `aria-expanded` de pe perch spune
 * ridicat / coborat, adica alt lucru. Deci: `wide`.
 *
 * Si nu e `requestFullscreen()`: aia ia tot ecranul si ascunde si browserul si
 * portalul. Aici panoul creste CAT LOCUL PORTALULUI si ramane un element al
 * paginii - barile portalului si ale browserului stau unde stau.
 */
const WIDE_KEY = 'od_ai_wide'
/*
 * Marit, panoul se imparte: chatul in stanga, "Live commands" in dreapta (vezi
 * `LiveCommands.tsx`). Dreapta e MAI INGUSTA decat jumatatea - 35% implicit,
 * trasa cu separatorul intre 25% si 50%. Latimea si ascuns / aratat se tin in
 * browser, ca celelalte alegeri ale sertarului.
 */
const LIVE_KEY = 'od_ai_live'
const LIVE_W_KEY = 'od_ai_live_w'
export const LIVE_MIN = 25
export const LIVE_MAX = 50
export const LIVE_DEFAULT = 35

export function clampLive(pct: number): number {
  if (!Number.isFinite(pct)) return LIVE_DEFAULT
  return Math.min(LIVE_MAX, Math.max(LIVE_MIN, Math.round(pct * 10) / 10))
}

type Bubble = {
  key: number
  who: 'ai' | 'user'
  text: string
  steps: AiStep[]
  /* Ora mesajului, cand o stim: din istoric (`at`) sau clipa trimiterii. */
  at?: string
}

type Wait = { what: string; note: string; since: number }

/*
 * AL DOILEA ADAM, ALATURI. O conversatie deschisa intr-un panou in STANGA
 * sertarului, de aceeasi inaltime, cu firul si caseta ei. Cate incap pe ecran
 * (sub SIDE_MIN_SCREEN niciunul - se deschide in sertar, ca inainte); rularile
 * pornite de aici intra in aceeasi limita "Adam in parallel".
 */
type Side = { convId: number; scope: AiScope; title: string; bubbles: Bubble[]; input: string }
export const SIDE_W = 380
export const SIDE_GAP = 12
export const SIDE_MIN_SCREEN = 1100

/** Unde se vede o conversatie: sertarul si/sau panoul alaturat. */
type View = { bubbles(fn: (cur: Bubble[]) => Bubble[]): void; main: boolean }

/** Cate panouri incap in stanga sertarului, si unde sta el (fara transformarea de ridicare). */
export function sideRoom(): { cap: number; left: number; top: number; height: number } {
  const el = document.getElementById('odAiDrawer')
  const w = el?.offsetWidth || 440
  const left = el?.offsetLeft || window.innerWidth - 18 - w
  const top = el?.offsetTop || 0
  const height = el?.offsetHeight || 0
  const cap = window.innerWidth < SIDE_MIN_SCREEN ? 0 : Math.max(0, Math.floor((left - SIDE_GAP) / (SIDE_W + SIDE_GAP)))
  return { cap, left, top, height }
}

/* HH:MM din `at` ("2026-09-30 14:05:12" sau ISO); gol daca nu se poate citi. */
export function msgTime(at: string | undefined): string {
  const m = /(?:^|[ T])(\d{2}):(\d{2})/.exec(at ?? '')
  return m ? `${m[1]}:${m[2]}` : ''
}

function clockNow(): string {
  const d = new Date()
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function ls(key: string): string {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function lsSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* fara localStorage ramane doar pe sesiunea asta */
  }
}

/* Vorbele lui - `SAY_*` din od_ai.js, neschimbate. */
const SAY_KEY = 'odAiSayNext'
const SAY_NEWS_KEY = 'odAiSaidNews'
const SAY_LAST_KEY = 'odAiSayLast'
const SAY_FRESH_DAY_KEY = 'odAiFreshDay'
const SAY_FRESH_VER_KEY = 'odAiFreshVer'
const SAY_MIN_WAIT = 60000
const SAY_MAX_WAIT = 300000
const SAY_MIN_SHOW = 10000
const SAY_MAX_SHOW = 20000
const SAY_LINES = [
  'Back at IT.',
  'Coffee first, then the fleet?',
  '{me}? I am right here.',
  'Still watching the estate.',
  'Ask me what changed last night.',
  'I read the logs, you drink the coffee.',
  'Nothing on fire. That I know of.',
  'Ask me which machines missed the patch.',
  'I never sleep. I do idle, though.',
  'Ask me why a machine went quiet.',
  'Ping me before the user does.',
  'One question here saves ten clicks there.',
  'Ask me who touched that server last.',
  'Reboot? Let us look first.',
  'I speak Windows, Linux and Mac.',
  'Ask me about disks filling up.',
  'The fleet is quiet. Suspiciously quiet.',
  'Ask me about last night failed logins.',
  'I read every agent log so you do not have to.',
  'Need a machine whole story? Ask.',
  'Ask me which alarms come back every week.',
  'Coffee and an IT admin: a good pair.',
  'I can propose the fix. You approve it.',
  'Ask me what this error actually means.',
  'Your fleet, in one sentence. Just ask.',
  'Ask me who is offline right now.',
  'Ready when you are, {me}.',
  'Uptime is a team sport.',
  'Ask me what ran on that machine today.',
  'I can check all of them at once.',
]

function sayPick(me: string): string {
  const pool: string[] = []
  for (const line of SAY_LINES) {
    if (line.includes('{me}')) {
      if (!me) continue
      pool.push(line.replace('{me}', me))
      continue
    }
    pool.push(line)
  }
  return pool.length ? pool[Math.floor(Math.random() * pool.length)]! : ''
}

function sayRand(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1))
}

function sayWhen(next: number): void {
  lsSet(SAY_KEY, String(next))
}

function sayToday(): string {
  const d = new Date()
  const pad = (n: number) => (n < 10 ? '0' + n : String(n))
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}

/** Vestea pe care ar spune-o ACUM, sau sirul gol: una pe zi, niciodata aceeasi livrare. */
function freshPick(rows: { version?: string; text?: string }[]): string {
  const said = ls(SAY_FRESH_VER_KEY).trim()
  if (ls(SAY_FRESH_DAY_KEY) === sayToday()) return ''
  for (const row of rows) {
    if (row && row.text && row.version !== said) {
      lsSet(SAY_FRESH_VER_KEY, String(row.version ?? ''))
      lsSet(SAY_FRESH_DAY_KEY, sayToday())
      return 'New: ' + row.text
    }
  }
  return ''
}

/** Masina din adresa fisei (`/inventoree/device/8`, `/proactivitee/device/8`...), sau 0. */
function deviceOnPage(pathname: string): number {
  const m = pathname.match(/\/device\/(\d+)(?:\/|$)/)
  return m ? Number(m[1]) : 0
}

/*
 * ASEZAREA LA RIDICARE - `fitAboveBar()` din od_ai.js, partea de desktop.
 *
 * Foaia stie doar 440x460 si `--od-ai-foot` de 39px. In portal, la ridicare
 * (si la incarcare, la fiecare redimensionare si dupa fiecare desenare de
 * tabel) scriptul masoara bara de jos, antetul fix si capul de tabel, si
 * pune panoul INLINE: `bottom` la 18px peste bara, `height` de sub antet
 * (sau de sub capul tabelului) pana la bara. Tot atunci ii spune foii cat e
 * bara (`--od-ai-foot`), cat e Adam de inalt (`--od-ai-perch-h`) si unde sta
 * pe latime (`--od-ai-perch-right`: intre primele doua insigne, sau la 12px
 * stanga de grupul lor). Fara asta panoul ramanea cat scrie in foaie, iar
 * Adam statea in colt, nu pe bara intre insigne.
 *
 * Nu s-a adus partea de tastatura de pe telefon (`fitToKeyboard`, `restBox`,
 * `screenBusy`): aici nu exista inca antetul care se stinge cat scrii.
 */
const COLUMNS_GAP = 12
const BAR_GAP = 18
const MIN_HEIGHT = 300

function visible(el: Element | null | undefined): el is HTMLElement {
  return el instanceof HTMLElement && el.getClientRects().length > 0
}

function fitAboveBar(): void {
  if (typeof window === 'undefined') return
  const root = document.documentElement
  let foot: HTMLElement | null = null
  for (const el of document.querySelectorAll('#pageFooter, #od-mbar')) {
    if (!visible(el)) continue
    if (!foot || el.getBoundingClientRect().top < foot.getBoundingClientRect().top) foot = el
  }
  const head = document.querySelector('#page-header .fixed-header')
  const headH = head ? Math.round(head.getBoundingClientRect().height) : 0
  /* Masura antetului pleaca si in foaie: panoul marit se opreste sub el, si o
     face din CSS, fara stiluri in linie - vezi mai jos. */
  root.style.setProperty('--od-ai-head-h', `${headH}px`)

  let footH = 0
  if (foot) {
    const fbox = foot.getBoundingClientRect()
    if (fbox.height > 0) {
      const below = parseFloat(getComputedStyle(foot).bottom)
      footH = Math.round(fbox.height + (Number.isNaN(below) ? 0 : below))
    }
  }
  root.style.setProperty('--od-ai-foot', `${footH}px`)

  const perch = document.getElementById('odAiPerch')
  const perchBox = perch ? perch.getBoundingClientRect() : null
  if (perchBox && perchBox.height > 0) root.style.setProperty('--od-ai-perch-h', `${Math.round(perchBox.height)}px`)

  let cmds: HTMLElement | null = [...document.querySelectorAll('#pageFooter .tk-footer-commands')].find(visible) ?? null
  if (!cmds) cmds = [...document.querySelectorAll('.od-mbar-fold')].find(visible) ?? null

  let right = 26
  let placed = false
  const perchW = perchBox ? perchBox.width : 0
  const badges = cmds ? [...cmds.querySelectorAll('#od-inflight > .badge')].filter(visible) : []
  if (badges.length >= 2 && perchW > 0) {
    const first = badges[0]!.getBoundingClientRect()
    const second = badges[1]!.getBoundingClientRect()
    const middle = (first.right + second.left) / 2
    if (middle > 0) {
      right = Math.round(window.innerWidth - middle - perchW / 2)
      placed = true
    }
  }
  if (!placed && cmds) {
    const cbox = cmds.getBoundingClientRect()
    if (cbox.width > 0) right = Math.round(window.innerWidth - cbox.left + 12)
  }
  if (right > window.innerWidth / 2 || right < 0) right = 26
  root.style.setProperty('--od-ai-perch-right', `${right}px`)

  const drawer = document.getElementById('odAiDrawer')

  /*
   * CAT E MARIT, SOCOTEALA DE MAI JOS TACE - si stilurile ei se STERG.
   *
   * Asezarea normala scrie `top`/`bottom`/`height` in linie, iar un stil in
   * linie bate orice clasa din foaie: o stare marita pusa pe `.is-wide` ar fi
   * fost calcata la prima redimensionare, la prima desenare de tabel sau la
   * prima schimbare de pagina. Deci marimea nu se lupta cu socoteala: aici se
   * opreste scrisul in linie si se curata ce a ramas, iar geometria marita
   * ramane numai a foii.
   *
   * MASURATORILE de mai sus se fac oricum, si bine ca se fac: panoul marit isi
   * ia marginile din `--od-ai-head-h` si `--od-ai-foot`, deci creste si se
   * strange corect cand fereastra se schimba, fara sa se atinga nimeni de
   * `style`.
   */
  if (drawer && drawer.classList.contains('is-wide')) {
    drawer.style.top = ''
    drawer.style.bottom = ''
    drawer.style.height = ''
    return
  }

  const thead = document.querySelector('table.dataTable thead, .dataTables_scrollHead')
  let top = headH + 72
  if (thead) {
    const box = thead.getBoundingClientRect()
    if (box.bottom > headH && box.bottom < window.innerHeight * 0.6) top = Math.round(box.bottom) + COLUMNS_GAP
  }
  const bottom = footH + BAR_GAP
  const height = Math.max(MIN_HEIGHT, window.innerHeight - top - bottom)
  if (drawer) {
    drawer.style.top = 'auto'
    drawer.style.bottom = `${bottom}px`
    drawer.style.height = `${height}px`
  }
}

/* Raspunsul despre ce e nou se tine pe pagina: bula bate din minut in minut. */
let freshCache: { version: string; text: string }[] | null = null

/*
 * MAI MULTI ADAM DEODATA. Fiecare intrebare trimisa e o RULARE a ei: fluxul ei,
 * bula ei in bara de jos, oprirea ei. Fereastra arata o singura conversatie;
 * o rulare a carei conversatie nu e in fereastra merge mai departe in fundal
 * si scrie numai in bula ei. Cate pot lucra deodata spune serverul
 * (`running.parallel`, implicit 1 - adica exact ca inainte: cat lucreaza una,
 * nu se trimite alta).
 */
type AdamLive = AdamLiveBubble & {
  since: number
  /* a lasat o propunere de schimbare - la sfarsit asteapta aprobarea omului */
  approval: boolean
  endedAt: number
  /* FLUXUL TINUT IN RULARE, nu in fereastra: textul de pana acum, ce face
     acum si nota. Orice fereastra care arata conversatia (sertarul, panoul
     alaturat, o redeschidere din bula) il deseneaza de aici - si il vede
     intreg din clipa in care se deschide, nu doar ce vine dupa. */
  text: string
  what: string
  note: string
}

/*
 * Rularile traiesc in afara componentei: o rulare porneste un flux care tine
 * minute intregi, iar fluxul ei trebuie sa ajunga la ORICE fereastra care ii
 * arata conversatia - si dupa ce sertarul s-a montat din nou.
 */
const runStore = {
  lives: [] as AdamLive[],
  subs: new Set<() => void>(),
  controllers: new Map<number, AbortController>(),
  key: 1,
}
function setRunLives(fn: (cur: AdamLive[]) => AdamLive[]): void {
  runStore.lives = fn(runStore.lives)
  for (const f of runStore.subs) f()
}
function subscribeRuns(f: () => void): () => void {
  runStore.subs.add(f)
  return () => {
    runStore.subs.delete(f)
  }
}
const readRuns = () => runStore.lives
/** Probele: fiecare porneste fara rulari ramase de la cea dinainte. */
export function resetAdamRuns(): void {
  runStore.lives = []
  runStore.controllers.clear()
}

/** Ce vede fereastra din rularea care lucreaza pe conversatia ei. */
function overlay(bubbles: Bubble[], live: AdamLive | undefined): Bubble[] {
  if (!live || live.text === '') return bubbles
  return [...bubbles, { key: -live.key, who: 'ai', text: live.text, steps: [] }]
}
function waitFromLive(live: AdamLive): Wait {
  return { what: live.what, note: live.note, since: live.since }
}

function toBubbles(messages: AiMessage[], next: () => number): Bubble[] {
  return messages.map((m) => ({
    key: next(),
    who: m.role === 'assistant' ? 'ai' : 'user',
    text: m.text,
    steps: m.steps ?? [],
    at: m.at,
  }))
}

function elapsed(seconds: number): string {
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

export function AdamDrawer() {
  const location = useLocation()
  const navigate = useNavigate()
  const queries = useQueryClient()
  const [open, setOpen] = useState(() => ls(UP_KEY) === '1')
  /* Marit peste portal, si tinut minte pe browserul omului (vezi WIDE_KEY). */
  const [wide, setWide] = useState(() => ls(WIDE_KEY) === '1')
  /* Impartirea din starea marita (vezi LIVE_KEY): aratat implicit. */
  const [liveOn, setLiveOn] = useState(() => ls(LIVE_KEY) !== '0')
  const [liveW, setLiveW] = useState(() => (ls(LIVE_W_KEY) === '' ? LIVE_DEFAULT : clampLive(Number(ls(LIVE_W_KEY)))))
  const [dragging, setDragging] = useState(false)
  const split = useRef<HTMLDivElement>(null)
  /* Pasii fluxului pentru discutia de acum; `livePulse` creste la fiecare
     `step_end`, ca panoul sa citeasca iar din sesiune. */
  const [liveSteps, setLiveSteps] = useState<LiveStep[]>([])
  const [livePulse, setLivePulse] = useState(0)
  const [follow, setFollow] = useState<'dynamic' | 'static'>(() => (ls(FOLLOW_KEY) === 'static' ? 'static' : 'dynamic'))
  const [tier, setTier] = useState('')
  const [say, setSay] = useState<{ text: string; news: boolean; on: boolean } | null>(null)
  const [scope, setScope] = useState<AiScope>({
    scope: 'all',
    device_id: 0,
    conversation_id: 0,
  })
  const [deviceName, setDeviceName] = useState('')
  const [mode, setMode] = useState('')
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  /* Asteptarea unei rulari care NU e a ferestrei (alta fila, o reincarcare);
     a rularilor de aici se citeste din rulare (`waitFromLive`). */
  const [wait, setWait] = useState<Wait | null>(null)
  const [runs, setRuns] = useState<AiRun[]>([])
  const [unseen, setUnseen] = useState(0)
  const [panel, setPanel] = useState<{
    title: string
    kind: 'history' | 'commands'
  } | null>(null)
  /* Meniul de aparate sta in bara scopului, sub buton, ca in portal
     (`#odAiDeviceMenu` in `.od-ai-device`, position: relative) - nu in panoul
     lateral, unde `position: absolute; top: 100%` il arunca sub sertar. */
  const [deviceMenu, setDeviceMenu] = useState(false)
  const [convs, setConvs] = useState<AiConversation[]>([])
  const [devices, setDevices] = useState<AiDevice[]>([])
  const [deviceQuery, setDeviceQuery] = useState('')
  const [commands, setCommands] = useState<AiSessionCommand[]>([])
  const [made, setMade] = useState<{ commands: number; changes: number }>({
    commands: 0,
    changes: 0,
  })
  const [input, setInput] = useState('')
  const [followNote, setFollowNote] = useState(false)
  const [modeMenu, setModeMenu] = useState(false)
  const [gate, setGate] = useState<{ needCode: boolean; sentTo: string; error: string } | null>(null)
  const [gateAgree, setGateAgree] = useState(false)
  const [gateCode, setGateCode] = useState('')
  const [gateBusy, setGateBusy] = useState(false)
  const [mcpOpen, setMcpOpen] = useState(false)
  const [tick, setTick] = useState(0)
  /* Rularile pornite din fereastra asta, si oprirea fiecareia. */
  useState(() => {
    /* Montat din nou fara niciun flux deschis: bulele vechi n-au ce cauta. */
    if (runStore.controllers.size === 0) runStore.lives = []
    return 0
  })
  const lives = useSyncExternalStore(subscribeRuns, readRuns, readRuns)
  const setLives = setRunLives
  /* Bulele inchise de om cat inca lucreaza: rularea merge, bula nu se mai arata. */
  const [hiddenKeys, setHiddenKeys] = useState<number[]>([])
  const [sides, setSides] = useState<Side[]>([])
  const sidesRef = useRef<Side[]>([])
  sidesRef.current = sides
  const [room, setRoom] = useState(() => ({ cap: 0, left: 0, top: 0, height: 0 }))
  const [parallel, setParallel] = useState(1)
  /* Serverul spune ca discutia din fereastra lucreaza, dar nu din fereastra asta. */
  const [serverBusy, setServerBusy] = useState(false)
  const viewRef = useRef(0)
  viewRef.current = scope.conversation_id
  const chat = useRef<HTMLDivElement>(null)
  const tiers = useQuery({ ...aiTiersQuery(), enabled: open })
  const modes = useQuery({ ...aiModesQuery(), enabled: open })

  const nextKey = () => runStore.key++

  const scroll = useCallback(() => {
    const el = chat.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  const load = useCallback(async (at: AiScope) => {
    try {
      const h = await aiHistory(at)
      setMode(h.mode)
      setBubbles(toBubbles(h.messages, nextKey))
      /* O rulare a ferestrei asteia pe discutia asta isi deseneaza singura asteptarea. */
      const mine = runStore.lives.find((l) => l.state === 'working' && l.convId === h.conversation_id)
      setServerBusy(h.running && !mine)
      setWait(null)
      if (h.running && !mine) {
        setWait({ what: h.progress?.note || 'still working', note: '', since: Date.now() })
        if (h.progress?.text) setBubbles((cur) => [...cur, { key: nextKey(), who: 'ai', text: h.progress!.text, steps: [] }])
      }
      setScope((cur) => ({ ...cur, conversation_id: h.conversation_id }))
      const s = await aiSession(at)
      setMade({ commands: s.commands.length, changes: s.changes.length })
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Adam could not be reached.', 'Adam Intop', 'error')
    }
  }, [])

  const refreshRunning = useCallback(async () => {
    try {
      const r = await aiRunning()
      setRuns(r.runs)
      if (typeof r.parallel === 'number' && r.parallel >= 1) setParallel(r.parallel)
    } catch {
      /* banda ramane cum era */
    }
  }, [])

  /* "Live commands" arata discutia DE ACUM: alta discutie, alti pasi. */
  useEffect(() => {
    setLiveSteps([])
  }, [scope.conversation_id, scope.device_id, scope.scope])

  /* Un pas din flux: `step` il deschide, `step_end` il inchide pe ultimul
     neinchis cu acelasi nume (uneltele ruleaza una dupa alta). */
  const onLiveEvent = (ev: AiStreamEvent) => {
    if (ev.type === 'step') {
      const step: LiveStep = {
        key: nextKey(),
        tool: ev.tool ?? '',
        at: Date.now(),
        done: false,
      }
      if (typeof ev.command === 'string' && ev.command !== '') step.command = ev.command
      if (typeof ev.device_id === 'number' && ev.device_id > 0) step.deviceId = ev.device_id
      setLiveSteps((cur) => [...cur, step])
      return
    }
    if (ev.type !== 'step_end') return
    setLiveSteps((cur) => {
      let at = -1
      for (let i = cur.length - 1; i >= 0; i--) {
        if (!cur[i]!.done && cur[i]!.tool === (ev.tool ?? '')) {
          at = i
          break
        }
      }
      if (at < 0) return cur
      const prev = cur[at]!
      const next: LiveStep = {
        ...prev,
        done: true,
        ms: typeof ev.ms === 'number' ? ev.ms : Date.now() - prev.at,
      }
      if (typeof ev.command_id === 'number' && ev.command_id > 0) next.commandId = ev.command_id
      if (typeof ev.draft_id === 'number' && ev.draft_id > 0) next.draftId = ev.draft_id
      if (typeof ev.device_id === 'number' && ev.device_id > 0 && !next.deviceId) next.deviceId = ev.device_id
      if (ev.error) next.error = true
      return [...cur.slice(0, at), next, ...cur.slice(at + 1)]
    })
    if (ev.command_id || ev.draft_id) setLivePulse((p) => p + 1)
  }

  /* Separatorul: tras cu mouse-ul, sau cu sagetile cand are focusul. */
  const setLive = (pct: number, keep: boolean) => {
    const next = clampLive(pct)
    setLiveW(next)
    if (keep) lsSet(LIVE_W_KEY, String(next))
  }
  const startDrag = (e: ReactMouseEvent) => {
    e.preventDefault()
    const box = split.current?.getBoundingClientRect()
    if (!box || box.width <= 0) return
    setDragging(true)
    let last = liveW
    const move = (m: MouseEvent) => {
      last = clampLive(((box.right - m.clientX) / box.width) * 100)
      setLiveW(last)
    }
    const up = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      setDragging(false)
      setLive(last, true)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }
  const toggleLive = () => {
    const next = !liveOn
    setLiveOn(next)
    lsSet(LIVE_KEY, next ? '1' : '0')
  }

  /* La ridicarea panoului: Dynamic pe pagina unei masini = despre ea intrebi. */
  useEffect(() => {
    if (!open) return
    lsSet(UP_KEY, '1')
    const onPage = deviceOnPage(location.pathname)
    const at: AiScope = follow === 'dynamic' && onPage > 0 ? { scope: 'device', device_id: onPage, conversation_id: 0 } : { ...scope, conversation_id: 0 }
    if (at.device_id !== scope.device_id || at.scope !== scope.scope) {
      setScope(at)
      setDeviceName(at.device_id > 0 ? `#${at.device_id}` : '')
    }
    void load(at)
    void refreshRunning()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) lsSet(UP_KEY, '0')
  }, [open])

  /* Treapta tinuta minte PE OM (cheia poarta user_id-ul), ca in loadTiers(). */
  useEffect(() => {
    const list = tiers.data?.tiers ?? []
    if (list.length < 1) return
    const key = tierKey(tiers.data?.user_id ?? 0)
    const wanted = ls(key)
    if (list.some((t) => t.key === wanted)) {
      setTier(wanted)
      return
    }
    setTier('')
    try {
      localStorage.removeItem(key)
    } catch {
      /* fara localStorage ramane doar pe sesiunea asta */
    }
  }, [tiers.data])

  /*
   * Ceasul vorbelor: se uita din zece in zece secunde, dar vorbeste rar -
   * pasul il tine `odAiSayNext`, in browser (sayTick / saySpeak / sayBell).
   */
  const sayRef = useRef<{ open: boolean; shown: boolean }>({ open, shown: false })
  sayRef.current.open = open
  useEffect(() => {
    let live = true
    let hideAt: ReturnType<typeof setTimeout> | null = null
    let offAt: ReturnType<typeof setTimeout> | null = null
    const me = (document.getElementById('odAiSay')?.getAttribute('data-me') ?? '').trim()

    const sayHide = () => {
      setSay((s) => (s ? { ...s, on: false } : s))
      offAt = setTimeout(() => {
        setSay(null)
        sayRef.current.shown = false
      }, 260)
      sayWhen(Date.now() + sayRand(SAY_MIN_WAIT, SAY_MAX_WAIT))
    }
    const sayShow = (text: string, fromBell: boolean) => {
      if (!text) {
        sayWhen(Date.now() + sayRand(SAY_MIN_WAIT, SAY_MAX_WAIT))
        return
      }
      if (!live) return
      sayRef.current.shown = true
      setSay({ text, news: fromBell, on: false })
      /* Un cadru intre "se vede" si "se aprinde", altfel tranzitia nu porneste. */
      setTimeout(() => setSay((s) => (s ? { ...s, on: true } : s)), 20)
      hideAt = setTimeout(sayHide, sayRand(SAY_MIN_SHOW, SAY_MAX_SHOW))
    }
    const sayBell = async () => {
      try {
        const r = await apiGet<BellPanel>('/notifications')
        const said = parseInt(ls(SAY_NEWS_KEY) || '0', 10) || 0
        let fresh: BellRow | null = null
        let freshId = 0
        for (const row of r?.notifications ?? []) {
          if (row.kind !== 'notification') continue
          const id = parseInt(row.id, 10) || 0
          if (id > said && (fresh === null || id > freshId)) {
            fresh = row
            freshId = id
          }
        }
        const text = fresh ? (fresh.text || fresh.title || '').trim() : ''
        if (text) {
          lsSet(SAY_NEWS_KEY, String(freshId))
          lsSet(SAY_LAST_KEY, 'news')
          sayShow(text, true)
          return
        }
      } catch {
        /* Clopotelul nu raspunde: vorbele lui nu depind de el. */
      }
      lsSet(SAY_LAST_KEY, 'line')
      sayShow(sayPick(me), false)
    }
    const saySpeak = async () => {
      /* Dupa o instiintare vine OBLIGATORIU o vorba de-a lui. */
      if (ls(SAY_LAST_KEY) === 'news') {
        lsSet(SAY_LAST_KEY, 'line')
        sayShow(sayPick(me), false)
        return
      }
      /* Ce e nou trece inaintea clopotelului, dar numai o data pe zi. */
      let fresh = ''
      if (ls(SAY_FRESH_DAY_KEY) !== sayToday()) {
        if (freshCache === null) {
          try {
            const r = await apiGet<{ news: { version: string; text: string }[] }>('/change_log/news')
            freshCache = r?.news ?? []
          } catch {
            freshCache = []
          }
        }
        fresh = freshPick(freshCache)
      }
      if (fresh) {
        lsSet(SAY_LAST_KEY, 'news')
        sayShow(fresh, true)
        return
      }
      await sayBell()
    }
    const sayTick = () => {
      if (!live) return
      /* Numai cat e coborat, si numai cat fereastra e la vedere. */
      if (sayRef.current.open || document.hidden || sayRef.current.shown) return
      const due = parseInt(ls(SAY_KEY) || '0', 10) || 0
      if (!due) {
        /* Prima data pe browserul asta: nu chiar in clipa incarcarii. */
        sayWhen(Date.now() + sayRand(SAY_MIN_WAIT, SAY_MAX_WAIT))
        return
      }
      if (Date.now() >= due) void saySpeak()
    }
    const every = setInterval(sayTick, 10000)
    const first = setTimeout(sayTick, 3000)
    return () => {
      live = false
      clearInterval(every)
      clearTimeout(first)
      if (hideAt) clearTimeout(hideAt)
      if (offAt) clearTimeout(offAt)
    }
  }, [])

  /* Cat sta de vorba cu tine, nu mai vorbeste singur. */
  useEffect(() => {
    if (!open) return
    setSay(null)
    sayRef.current.shown = false
  }, [open])

  /* Asezarea: la incarcare, la ridicare, la redimensionare, cand se schimba
     pagina (alt subsol, alt tabel) si cand bara de jos isi schimba masura
     (insignele vin din JS, dupa prima cerere - ca `draw.dt` in portal). */
  useEffect(() => {
    fitAboveBar()
    const raf = window.requestAnimationFrame(fitAboveBar)
    const onResize = () => fitAboveBar()
    window.addEventListener('resize', onResize)
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => fitAboveBar())
      for (const el of document.querySelectorAll('#pageFooter, #pageFooter .tk-footer-commands, table.dataTable thead')) ro.observe(el)
    }
    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
      ro?.disconnect()
    }
    /* `wide` e in lista dinadins: la micsorare socoteala trebuie sa scrie iar
       inaltimea in linie, altfel panoul ar ramane cat scrie in foaie (460px). */
  }, [open, location.pathname, wide])

  /*
   * PE TELEFON NU EXISTA STARE MARITA.
   *
   * Sub 768px panoul tine oricum cat ecranul pe lat (`left: 8px; right: 8px`),
   * si butonul se ascunde din foaie - deci starea n-ar mai avea de unde sa fie
   * intoarsa. Ca sa nu ramana nimeni cu un panou marit si fara buton, la latime
   * mica se strange singur; ce a ales omul ramane scris in localStorage, deci pe
   * ecran lat il gaseste tot marit.
   */
  useEffect(() => {
    const check = () => {
      if (window.innerWidth < 768) setWide(false)
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  /* Ridicat de om: cursorul intra in camp; Escape il micsoreaza daca e marit,
     altfel il trimite inapoi jos. */
  const wideRef = useRef(wide)
  wideRef.current = wide
  useEffect(() => {
    if (!open) return
    const focus = window.setTimeout(() => document.getElementById('odAiInput')?.focus(), 200)
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return
      /* Un panou care acopera portalul si nu se lasa strans de la tastatura e o
         capcana. Prima apasare il micsoreaza, a doua il coboara. */
      if (wideRef.current) {
        setWide(false)
        lsSet(WIDE_KEY, '0')
        return
      }
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => {
      window.clearTimeout(focus)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /* O apasare in afara meniului de mod il inchide (meniul opreste clicul). */
  useEffect(() => {
    if (!modeMenu && !deviceMenu) return
    const away = () => {
      setModeMenu(false)
      setDeviceMenu(false)
    }
    document.addEventListener('click', away)
    return () => document.removeEventListener('click', away)
  }, [modeMenu, deviceMenu])

  /* Insigna: cate raspunsuri au venit de cand ai plecat. */
  useEffect(() => {
    let live = true
    const poll = async () => {
      try {
        const p = await aiPending(Number(ls(SEEN_KEY)) || 0)
        if (!live) return
        setUnseen(p.unseen)
        if (open) lsSet(SEEN_KEY, String(p.last_assistant_id))
      } catch {
        /* fara drept sau fara server: insigna tace */
      }
    }
    void poll()
    const id = window.setInterval(() => void poll(), open ? 15000 : 60000)
    return () => {
      live = false
      window.clearInterval(id)
    }
  }, [open])

  const ticking = !!wait || lives.some((l) => l.state === 'working')
  useEffect(() => {
    if (!ticking) return
    const id = window.setInterval(() => setTick((t) => t + 1), 1000)
    return () => window.clearInterval(id)
  }, [ticking])

  useEffect(scroll, [bubbles, wait, scroll])

  /* "Gata" dispare singura dupa cateva secunde; aprobarea si eroarea asteapta omul. */
  useEffect(() => {
    const done = lives.filter((l) => l.state === 'done')
    if (done.length === 0) return
    const next = Math.min(...done.map((l) => l.endedAt + BUBBLE_LINGER)) - Date.now()
    const id = window.setTimeout(
      () => setLives((cur) => cur.filter((l) => !(l.state === 'done' && l.endedAt + BUBBLE_LINGER <= Date.now()))),
      Math.max(0, next),
    )
    return () => window.clearTimeout(id)
  }, [lives])

  /* Discutia din fereastra lucreaza pe server, nu din fereastra asta (alta fila,
     o reincarcare): se reciteste din cateva in cateva secunde, pana se termina. */
  useEffect(() => {
    if (!serverBusy || !open) return
    const at = scope
    const id = window.setInterval(() => {
      void aiHistory(at)
        .then((h) => {
          if (viewRef.current !== h.conversation_id) return
          if (h.running) {
            if (h.progress?.note) setWait((w) => (w ? { ...w, what: h.progress!.note } : { what: h.progress!.note, note: '', since: Date.now() }))
            /* Textul de pana acum, cat il stie serverul - nu abia la sfarsit. */
            const sofar = h.progress?.text ?? ''
            if (sofar !== '') setBubbles([...toBubbles(h.messages, nextKey), { key: nextKey(), who: 'ai', text: sofar, steps: [] }])
            return
          }
          setBubbles(toBubbles(h.messages, nextKey))
          setWait(null)
          setServerBusy(false)
          void refreshRunning()
        })
        .catch(() => undefined)
    }, 2000)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverBusy, open, scope.conversation_id])

  const mainView: View = { bubbles: (fn) => setBubbles(fn), main: true }
  const sideView = (conv: number): View => ({
    bubbles: (fn) => setSides((cur) => cur.map((x) => (x.convId === conv ? { ...x, bubbles: fn(x.bubbles) } : x))),
    main: false,
  })
  /* Unde se vede ACUM conversatia unei rulari: in sertar, alaturi, ori nicaieri. */
  const viewsOf = (conv: number): View[] => [
    ...(viewRef.current === conv ? [mainView] : []),
    ...(conv > 0 && sidesRef.current.some((x) => x.convId === conv) ? [sideView(conv)] : []),
  ]

  const finishRun = (text: string, steps: AiStep[], at: AiScope = scope, views: View[] = [mainView]) => {
    for (const v of views)
      v.bubbles((cur) => {
        const last = cur[cur.length - 1]
        if (last && last.who === 'ai' && last.text === '') return [...cur.slice(0, -1), { ...last, text, steps, at: clockNow() }]
        return [...cur, { key: nextKey(), who: 'ai', text, steps, at: clockNow() }]
      })
    if (views.some((v) => v.main)) {
      setWait(null)
      void aiSession(at)
        .then((s) => setMade({ commands: s.commands.length, changes: s.changes.length }))
        .catch(() => undefined)
      setLivePulse((p) => p + 1)
    }
    void refreshRunning()
    void queries.invalidateQueries({ queryKey: ['assistant', 'activity'] })
  }

  const patchLive = (key: number, patch: Partial<AdamLive>) => setLives((cur) => cur.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  /* Pasul de acum, si urma lui in bula extinsa. */
  const stepLive = (key: number, step: string, patch: Partial<AdamLive> = {}) =>
    setLives((cur) => cur.map((l) => (l.key === key ? { ...l, ...patch, step, trail: pushTrail(l.trail, step) } : l)))

  const working = lives.filter((l) => l.state === 'working').length
  const viewLive = lives.find((l) => l.state === 'working' && l.convId === scope.conversation_id)
  /* Discutia din fereastra lucreaza (aici sau, dupa server, in alta parte)... */
  const busy = !!viewLive || serverBusy
  /* ...sau omul are deja atatea rulari cate are voie. */
  const atLimit = working >= parallel

  const send = async () => {
    const message = input.trim()
    if (message === '' || busy || atLimit) return
    setInput('')
    let at = scope
    /* In paralel, fiecare rulare trebuie sa stie a cui conversatie e: una
       noua, fara numar inca, si-l ia acum (ca "+ New"). Cu limita 1 nu se
       schimba nimic - serverul o porneste, ca inainte. */
    if (parallel > 1 && at.conversation_id < 1) {
      try {
        const out = await aiReset(at)
        if (out.conversation_id > 0) {
          at = { ...at, conversation_id: out.conversation_id }
          viewRef.current = at.conversation_id
          setScope(at)
        }
      } catch {
        /* ramane fara numar: serverul o porneste la trimitere */
      }
    }
    await run(at, message)
  }

  /*
   * O rulare. Fluxul ei se scrie IN RULARE (text, pas, nota); fereastra care ii
   * arata conversatia il deseneaza de acolo, oricare ar fi ea si oricand s-ar fi
   * deschis. La sfarsit raspunsul intra in firul ferestrelor care o arata; cele
   * care nu, il gasesc in istoric cand o deschid.
   */
  const run = async (at: AiScope, message: string) => {
    const runConv = at.conversation_id
    const views = () => viewsOf(runConv)
    const here = () => views().length > 0
    const inMain = () => viewRef.current === runConv
    const liveKey = nextKey()
    setLives((cur) => [
      ...cur,
      {
        key: liveKey,
        convId: runConv,
        title: shortTitle(message),
        step: 'thinking',
        state: 'working',
        since: Date.now(),
        approval: false,
        endedAt: 0,
        trail: ['thinking'],
        text: '',
        what: 'thinking',
        note: '',
      },
    ])
    const endLive = (state: 'done' | 'error') =>
      setLives((cur) => cur.map((l) => (l.key === liveKey ? { ...l, state: state === 'done' && l.approval ? 'approval' : state, endedAt: Date.now() } : l)))
    for (const v of views()) v.bubbles((cur) => [...cur, { key: nextKey(), who: 'user', text: message, steps: [], at: clockNow() }])
    const controller = new AbortController()
    runStore.controllers.set(liveKey, controller)
    let full = ''
    const steps: AiStep[] = []
    let finished = false
    let drawn = 0
    let late: ReturnType<typeof setTimeout> | null = null
    /* Textul se scrie cel mult o data la 100ms - dar ULTIMA bucata nu se pierde:
       daca nu e randul ei acum, se scrie la capatul celor 100ms. */
    const drawText = () => {
      const now = Date.now()
      if (now - drawn > 100) {
        drawn = now
        if (late) clearTimeout(late)
        late = null
        patchLive(liveKey, { text: full })
        return
      }
      if (late) return
      late = setTimeout(() => {
        late = null
        drawn = Date.now()
        patchLive(liveKey, { text: full })
      }, 100)
    }
    const stopDrawing = () => {
      if (late) clearTimeout(late)
      late = null
    }
    try {
      const out = await aiStream(at, message, tier, controller.signal, (ev) => {
        if (ev.type === 'step_end' && ev.draft_id) patchLive(liveKey, { approval: true })
        if ((ev.type === 'step' || ev.type === 'step_end') && inMain()) onLiveEvent(ev)
        if (ev.type === 'delta') {
          full += ev.text ?? ''
          drawText()
        } else if (ev.type === 'note') {
          const note = (ev.text ?? '').replace(/\s+/g, ' ').trim()
          if (note !== '') stepLive(liveKey, shortTitle(note, 80), { note })
          else patchLive(liveKey, { note })
        } else if (ev.type === 'step') {
          steps.push({ tool: ev.tool ?? '' })
          const what = ev.tool ? `running ${ev.tool}` : 'working'
          stepLive(liveKey, what, { what })
        } else if (ev.type === 'done') {
          finished = true
          stopDrawing()
          full = ev.text || full
          endLive('done')
          void refreshRunning()
          if (here()) finishRun(full, steps, at, views())
        } else if (ev.type === 'error') {
          finished = true
          stopDrawing()
          endLive('error')
          if (here()) finishRun(ev.text ?? 'Something went wrong.', [], at, views())
        }
      })
      if (!out.streamed) {
        if (out.fallback?.text) {
          endLive('error')
          if (here()) finishRun(String(out.fallback.text), [], at, views())
          return
        }
        const answer = await aiAsk(at, message, tier)
        endLive(answer.error ? 'error' : 'done')
        if (here()) finishRun(answer.text ?? 'Something went wrong.', answer.steps ?? [], at, views())
        return
      }
      if (!finished) {
        /* Fluxul s-a inchis fara incheiere: raspunsul se scrie oricum in
           conversatie; se reciteste de acolo. */
        stopDrawing()
        endLive('done')
        if (inMain()) {
          await load(at)
          setWait(null)
        }
        if (sidesRef.current.some((x) => x.convId === runConv)) await reloadSide(runConv)
      }
    } catch (error) {
      stopDrawing()
      if (controller.signal.aborted) {
        endLive('done')
        if (here()) finishRun(full !== '' ? full : 'Stopped.', steps, at, views())
      } else {
        endLive('error')
        if (here()) finishRun(error instanceof Error ? error.message : 'Adam could not be reached.', [], at, views())
      }
    } finally {
      runStore.controllers.delete(liveKey)
    }
  }

  /* Opreste rularea unei conversatii, oricare ar fi (bula, panoul alaturat). */
  const stopConv = async (conv: number) => {
    try {
      await aiCancel(scope, conv)
    } catch {
      /* semnalul de oprire nu s-a scris; fluxul se rupe oricum */
    }
    const live = runStore.lives.find((l) => l.state === 'working' && l.convId === conv)
    if (live) runStore.controllers.get(live.key)?.abort()
    void refreshRunning()
  }

  const stop = async () => {
    try {
      await aiCancel(scope)
    } catch {
      /* semnalul de oprire nu s-a scris; fluxul se rupe oricum */
    }
    const live = runStore.lives.find((l) => l.state === 'working' && l.convId === scope.conversation_id)
    if (live) runStore.controllers.get(live.key)?.abort()
  }

  /*
   * Fereastra trece pe alta conversatie. O rulare de AICI se deseneaza singura,
   * din rulare. Una care lucreaza pe server si nu e a ferestrei (alta fila, o
   * reincarcare, bula venita din `running`) se urmareste prin recitire: altfel
   * fereastra ramanea fara asteptare si fara recitire, si raspunsul aparea abia
   * la urmatoarea deschidere, tot deodata.
   */
  const followServer = (convId: number) => {
    if (runStore.lives.some((l) => l.state === 'working' && l.convId === convId)) {
      setServerBusy(false)
      setWait(null)
      return
    }
    const r = runs.find((x) => x.conversation_id === convId && x.state === 'running')
    setServerBusy(!!r)
    setWait(r ? { what: `working on ${r.where}`, note: '', since: Date.now() - r.seconds * 1000 } : null)
  }

  const newConversation = async () => {
    const out = await aiReset(scope)
    viewRef.current = out.conversation_id
    setScope((cur) => ({ ...cur, conversation_id: out.conversation_id }))
    setServerBusy(false)
    setWait(null)
    setBubbles([])
    setMade({ commands: 0, changes: 0 })
    setPanel(null)
  }

  const pickScope = async (next: AiScope, name = '') => {
    viewRef.current = next.conversation_id
    setServerBusy(false)
    setScope(next)
    setDeviceName(name)
    setPanel(null)
    await load(next)
  }

  const openHistory = async () => {
    setPanel({ title: 'Past conversations', kind: 'history' })
    try {
      setConvs((await aiConversations()).conversations)
    } catch {
      setConvs([])
    }
  }

  const openConversation = async (id: number) => {
    const out = await aiOpenConversation(id)
    if (!out.ok) {
      toast(out.error ?? 'The conversation could not be opened.', 'Adam Intop', 'error')
      return
    }
    const deviceId = out.device_id ?? 0
    const next: AiScope = {
      scope: deviceId > 0 ? 'device' : 'all',
      device_id: deviceId,
      conversation_id: id,
    }
    viewRef.current = id
    setScope(next)
    followServer(id)
    setSides((cur) => cur.filter((x) => x.convId !== id))
    setDeviceName(deviceId > 0 ? `#${deviceId}` : '')
    setBubbles(toBubbles(out.messages ?? [], nextKey))
    setPanel(null)
    const s = await aiSession(next).catch(() => ({
      commands: [],
      changes: [],
    }))
    setMade({ commands: s.commands.length, changes: s.changes.length })
  }

  const searchDevices = async (q: string) => {
    setDeviceQuery(q)
    try {
      setDevices((await aiDevices(q)).devices)
    } catch {
      setDevices([])
    }
  }

  const act = async (what: 'save' | 'copy' | 'ticket' | 'schedule') => {
    if (what === 'schedule') {
      const last = [...bubbles].reverse().find((b) => b.who === 'user')
      void navigate({
        to: '/assistant/schedules',
        search: { q: last?.text ?? '', device_id: scope.device_id } as never,
      })
      return
    }
    if (what === 'ticket') {
      const out = await aiToTicket(scope)
      if (!out.ok) {
        toast(out.error ?? 'The ticket could not be opened.', 'Adam Intop', 'error')
        return
      }
      toast(`Ticket #${out.ticket_id} opened. A new conversation starts here.`, 'Adam Intop')
      await newConversation()
      return
    }
    const t = await aiTranscript(scope)
    if (!t.ok || !t.text) {
      toast(t.error ?? 'There is no conversation to save.', 'Adam Intop', 'error')
      return
    }
    if (what === 'copy') {
      try {
        await navigator.clipboard.writeText(t.text)
        toast('Copied.', 'Adam Intop')
      } catch {
        toast('The clipboard is not available here.', 'Adam Intop', 'error')
      }
      return
    }
    const blob = new Blob([t.text], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = t.filename ?? 'conversation.txt'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const openCommands = async () => {
    setPanel({
      title: 'Commands run from this conversation',
      kind: 'commands',
    })
    try {
      setCommands((await aiSessionCommands(scope)).rows)
    } catch {
      setCommands([])
    }
  }

  /*
   * Poarta Full Access: riscurile, invoirea si - cand serverul il cere -
   * codul de pe email. Un singur ecran, care creste cu un rand (`need_code`).
   */
  const openGate = (r: { need_code?: boolean; sent_to?: string; error?: string | null } | null) => {
    const needCode = !!r?.need_code
    if (!needCode) setGateAgree(false)
    setGateCode('')
    setGate({
      needCode,
      sentTo: r?.sent_to ?? '',
      error: r?.error && !r.sent_to ? r.error : '',
    })
    window.setTimeout(() => document.getElementById(needCode ? 'odAiGateCode' : 'odAiGateAgree')?.focus(), 80)
  }

  const sendMode = async (next: AgentMode, code = '') => {
    try {
      const r = await setAgentMode(next, code, code !== '' ? 'I agree' : '')
      if (r.ok) {
        setMode(next)
        setGate(null)
        return
      }
      if (r.need_code) {
        openGate(r)
        return
      }
      if (gate) setGate({ ...gate, error: r.error || 'The mode could not be changed.' })
      else toast(r.error || 'The mode could not be changed.', 'Adam Intop', 'error')
    } catch {
      if (gate) setGate({ ...gate, error: 'The request failed.' })
      else toast('The request failed.', 'Adam Intop', 'error')
    }
  }

  const switchMode = (next: AgentMode) => {
    setModeMenu(false)
    if (next === 'incident' && !window.confirm('Switch to INCIDENT mode? Pre-approved actions run IMMEDIATELY on machines, with no sign-off on each one.')) return
    /* Full Access: MEREU prin poarta, chiar daca sesiunea a confirmat deja. */
    if (next === 'full') {
      openGate(null)
      return
    }
    void sendMode(next)
  }

  const gateGo = async () => {
    setGateBusy(true)
    await sendMode('full', gate?.needCode ? gateCode.trim() : '')
    setGateBusy(false)
  }

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  /*
   * Treapta: goala inseamna ascuns; UNA singura se arata (e treapta pe care o
   * ai). Cea tinuta minte, altfel implicita firmei, altfel prima din lista -
   * ca `loadTiers()` din od_ai.js.
   */
  const tierList = tiers.data?.tiers ?? []
  const tierValue = tierList.some((t) => t.key === tier) ? tier : (tierList.find((t) => t.current)?.key ?? tierList[0]?.key ?? '')
  const tierDefault = tierList.find((t) => t.current)?.label ?? ''

  /*
   * Meniul e adevarul: vine de la server, filtrat dupa drepturi. Daca modul
   * scris pe firma nu e in el, insigna arata cel mai de sus la care are omul
   * dreptul (`paintMode` din od_ai.js).
   */
  const modeList = modes.data?.modes ?? []
  const shownMode = modeList.length > 0 && !modeList.some((m) => m.key === mode) ? modeList[modeList.length - 1]!.key : mode
  const modeLabel =
    {
      problem: 'Investigation',
      change: 'Change',
      incident: 'Incident',
      full: 'Full Access',
    }[shownMode] ??
    (shownMode || '…')
  const gateReady = gateAgree && (!gate?.needCode || gateCode.trim().length === 8)
  const live = runs.filter((r) => r.state === 'running').length

  /* Bulele: rularile de aici, plus ce lucreaza pe server din alta parte (alta fila). */
  const localNoId = lives.some((l) => l.state === 'working' && l.convId < 1)
  const liveBubbles: AdamLiveBubble[] = [
    /* ascunsa doar cat lucreaza: aprobarea si eroarea revin, fiindca cer ceva de la om */
    ...lives.filter((l) => !(l.state === 'working' && hiddenKeys.includes(l.key))),
    ...(localNoId
      ? []
      : runs
          .filter((r) => r.state === 'running' && !lives.some((l) => l.convId === r.conversation_id) && !hiddenKeys.includes(-r.conversation_id))
          .map((r) => ({
            key: -r.conversation_id,
            convId: r.conversation_id,
            title: shortTitle(r.question),
            step: `working on ${r.where}`,
            state: 'working' as const,
            since: Date.now() - r.seconds * 1000,
          }))),
  ]
  const openLive = (b: AdamLiveBubble) => {
    setOpen(true)
    if (b.state !== 'working') setLives((cur) => cur.filter((l) => l.key !== b.key))
    if (b.convId > 0 && b.convId !== scope.conversation_id) void openConversation(b.convId)
  }
  /* Inchisa cat lucreaza: rularea merge mai departe, bula doar nu se mai arata. */
  const closeLive = (b: AdamLiveBubble) => {
    if (b.state === 'working') setHiddenKeys((cur) => [...cur, b.key])
    else setLives((cur) => cur.filter((l) => l.key !== b.key))
  }
  const stopLive = (b: AdamLiveBubble) => void stopConv(b.convId)

  /* Panourile alaturate: masurate pe sertar, la ridicare si la redimensionare. */
  useEffect(() => {
    if (!open) return
    const measure = () => setRoom(sideRoom())
    measure()
    const raf = window.requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
    }
  }, [open, wide, sides.length, location.pathname])

  const reloadSide = async (conv: number) => {
    try {
      const out = await aiOpenConversation(conv)
      if (out.ok) setSides((cur) => cur.map((x) => (x.convId === conv ? { ...x, bubbles: toBubbles(out.messages ?? [], nextKey) } : x)))
    } catch {
      /* ramane ce era */
    }
  }
  /* "Open beside": conversatia intr-un panou in stanga sertarului. Fara loc
     (ecran ingust, sertar marit) se deschide in sertar, ca un clic pe bula. */
  const openBeside = async (b: AdamLiveBubble) => {
    const conv = b.convId
    if (conv < 1) return
    if (!open) setOpen(true)
    if (conv === viewRef.current || sidesRef.current.some((x) => x.convId === conv)) return
    const cap = sideRoom().cap
    if (cap < 1) {
      openLive(b)
      return
    }
    if (b.state !== 'working') setLives((cur) => cur.filter((l) => l.key !== b.key))
    const out = await aiOpenConversation(conv).catch(() => ({ ok: false as const, error: '' }))
    if (!out.ok) {
      toast(('error' in out && out.error) || 'The conversation could not be opened.', 'Adam Intop', 'error')
      return
    }
    const dev = ('device_id' in out ? out.device_id : 0) ?? 0
    const side: Side = {
      convId: conv,
      scope: { scope: dev > 0 ? 'device' : 'all', device_id: dev, conversation_id: conv },
      title: b.title || 'Adam',
      bubbles: toBubbles(('messages' in out ? out.messages : []) ?? [], nextKey),
      input: '',
    }
    setSides((cur) => [...cur.filter((x) => x.convId !== conv), side].slice(-cap))
  }
  /*
   * "Inapoi in sertar": panoul trece in sertar, iar conversatia din sertar ii
   * ia locul in stanga - SCHIMB, nu inlocuire. Inainte, conversatia din sertar
   * disparea de pe ecran; daca rularea ei se terminase, bula ei plecase deja,
   * si omul o mai gasea doar in istoric ("am pierdut un chat").
   */
  const toMain = async (conv: number) => {
    const prevConv = viewRef.current
    if (prevConv > 0 && prevConv !== conv) {
      const firstAsk = bubbles.find((b) => b.who === 'user')?.text ?? ''
      const title = runStore.lives.find((l) => l.convId === prevConv)?.title || shortTitle(firstAsk) || 'Adam'
      const moving = sidesRef.current.find((x) => x.convId === conv)
      const prev: Side = { convId: prevConv, scope: { ...scope, conversation_id: prevConv }, title, bubbles, input }
      /* si ce era inceput de scris trece odata cu conversatia lui */
      sidesRef.current = sidesRef.current.map((x) => (x.convId === conv ? prev : x))
      setSides((cur) => cur.map((x) => (x.convId === conv ? prev : x)))
      setInput(moving?.input ?? '')
    }
    await openConversation(conv)
  }
  const closeSide = (conv: number) => setSides((cur) => cur.filter((x) => x.convId !== conv))
  const sideInput = (conv: number, text: string) => setSides((cur) => cur.map((x) => (x.convId === conv ? { ...x, input: text } : x)))
  const sendSide = async (conv: number) => {
    const side = sidesRef.current.find((x) => x.convId === conv)
    if (!side) return
    const message = side.input.trim()
    const sideBusy = runStore.lives.some((l) => l.state === 'working' && l.convId === conv) || runs.some((r) => r.state === 'running' && r.conversation_id === conv)
    if (message === '' || sideBusy || atLimit) return
    sideInput(conv, '')
    await run(side.scope, message)
  }
  const besideOk = parallel > 1 && typeof window !== 'undefined' && window.innerWidth >= SIDE_MIN_SCREEN
  const shownWait = viewLive ? waitFromLive(viewLive) : wait
  const since = shownWait ? Math.round((Date.now() - shownWait.since) / 1000) : 0
  void tick

  /* Firul: acelasi element in amandoua asezarile. Marit, sta in stanga
     impartirii; altfel e exact unde era. */
  const chatView = (
    <AdamThread bubbles={overlay(bubbles, viewLive)} wait={shownWait} since={since} wide={wide} onStop={() => void stop()} chatRef={chat} id="odAiChat" />
  )

  return (
    <>
      <button
        type="button"
        id="odAiPerch"
        className={`od-ai-perch${open ? ' is-away' : ''}`}
        title="Adam Intop - ask the agent"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Adam size={46} title="Adam Intop" hands />
        <span className="od-ai-perch-badge" id="odAiPerchBadge" hidden={unseen < 1}>
          {unseen}
        </span>
      </button>

      {/* Bulele rularilor, pe bara de jos - cat panoul e coborat. */}
      {!open ? (
        <AdamLives lives={liveBubbles} onOpen={openLive} onClose={closeLive} onStop={stopLive} onBeside={besideOk ? (b) => void openBeside(b) : undefined} />
      ) : null}

      {/* Al doilea Adam (si al treilea, cat incap): in stanga sertarului, cat el de inalte. */}
      {open
        ? sides.slice(-room.cap).map((side, i, shown) => {
            const sideLive = lives.find((l) => l.state === 'working' && l.convId === side.convId)
            const serverRun = runs.find((r) => r.state === 'running' && r.conversation_id === side.convId)
            const sideWait: Wait | null = sideLive
              ? waitFromLive(sideLive)
              : serverRun
                ? { what: `working on ${serverRun.where}`, note: '', since: Date.now() - serverRun.seconds * 1000 }
                : null
            const sideBusy = !!sideLive || !!serverRun
            /* cel mai nou langa sertar, cele mai vechi spre stanga */
            const slot = shown.length - 1 - i
            const style: CSSProperties = {
              left: room.left - (slot + 1) * (SIDE_W + SIDE_GAP),
              top: room.top,
              height: room.height > 0 ? room.height : undefined,
              width: SIDE_W,
            }
            return (
              <aside key={side.convId} className="od-ai-side" data-conv={side.convId} style={style} aria-label={`Adam - ${side.title}`}>
                <div className="od-ai-head od-ai-side-head">
                  <Adam size={22} />
                  <span className="od-ai-side-title" title={side.title}>
                    {side.title}
                  </span>
                  <button
                    type="button"
                    className="od-ai-side-btn"
                    data-act="main"
                    title="Open in the main panel"
                    aria-label="Open in the main panel"
                    onClick={() => void toMain(side.convId)}
                  >
                    <i className="fas fa-expand-alt" aria-hidden="true"></i>
                  </button>
                  <button type="button" className="od-ai-side-btn" data-act="close" title="Close" aria-label="Close this panel" onClick={() => closeSide(side.convId)}>
                    &times;
                  </button>
                </div>
                <AdamThread
                  bubbles={overlay(side.bubbles, sideLive)}
                  wait={sideWait}
                  since={sideWait ? Math.round((Date.now() - sideWait.since) / 1000) : 0}
                  wide={false}
                  onStop={() => void stopConv(side.convId)}
                />
                <div className="od-ai-compose">
                  <div className="od-ai-compose-field">
                    <textarea
                      className="od-ai-side-input"
                      rows={2}
                      placeholder={
                        atLimit && !sideBusy ? `${working} Adam runs are working - your limit is ${parallel}. Wait for one to finish.` : 'Ask the agent...'
                      }
                      title="Enter sends · Shift+Enter new line"
                      value={side.input}
                      onChange={(e) => sideInput(side.convId, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          void sendSide(side.convId)
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="od-ai-send od-ai-side-send"
                      title="Send"
                      aria-label="Send"
                      disabled={sideBusy || atLimit}
                      onClick={() => void sendSide(side.convId)}
                    >
                      <i className="fas fa-paper-plane" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </aside>
            )
          })
        : null}

      {/* Ce spune Adam cand nu e chemat - `#odAiSay` din od_ai_drawer.php. v4 nu
          stie prenumele omului (`/me` nu-l da), deci randurile cu `{me}` se sar. */}
      <div
        className={`od-ai-say${say?.on ? ' is-on' : ''}${say?.news ? ' is-news' : ''}`}
        id="odAiSay"
        hidden={!say}
        aria-hidden="true"
        data-me=""
      >
        {say?.text ?? ''}
      </div>

      <aside id="odAiDrawer" className={`od-ai-drawer${open ? ' is-open' : ''}${wide ? ' is-wide' : ''}`} aria-hidden={!open}>
        <div className="od-ai-head">
          <Adam size={26} />
          <strong className="od-ai-title">Adam Intop</strong>
          <select
            className="od-ai-tier"
            id="odAiTier"
            title={tierDefault ? `The tier this question runs on (default: ${tierDefault})` : 'The tier this question runs on'}
            aria-label="Tier"
            hidden={tierList.length < 1}
            value={tierValue}
            onChange={(e) => {
              setTier(e.target.value)
              lsSet(tierKey(tiers.data?.user_id ?? 0), e.target.value)
            }}
          >
            {tierList.map((t) => (
              <option key={t.key} value={t.key} disabled={t.usable === false} title={t.usable === false ? t.why || 'this tier cannot answer yet' : ''}>
                {t.label}
                {t.usable === false ? ' (not ready)' : ''}
              </option>
            ))}
          </select>
          <div className="od-ai-follow-dock">
            <div className="od-ai-follow-wrap">
              <button
                type="button"
                className={`od-ai-follow${follow === 'static' ? ' is-static' : ''}`}
                id="odAiFollow"
                title={
                  follow === 'static'
                    ? 'Fixed: stays in this conversation wherever you go. Click for Dynamic.'
                    : 'Dynamic: follows the machine you are on. Click for Fixed.'
                }
                onClick={() => {
                  const next = follow === 'static' ? 'dynamic' : 'static'
                  setFollow(next)
                  lsSet(FOLLOW_KEY, next)
                }}
              >
                <i className={`fas ${follow === 'static' ? 'fa-lock' : 'fa-crosshairs'}`} aria-hidden="true"></i>{' '}
                <span id="odAiFollowName">{follow === 'dynamic' ? 'Dynamic' : 'Fixed'}</span>
              </button>
              <button
                type="button"
                className="od-ai-follow-why"
                id="odAiFollowWhy"
                aria-label="What Fixed and Dynamic mean"
                onClick={() => setFollowNote((v) => !v)}
              >
                <i className="fas fa-info" aria-hidden="true"></i>
              </button>
              <div className="od-ai-follow-note" id="odAiFollowNote" hidden={!followNote}>
                <p>
                  <strong>Dynamic</strong> - when you raise me on a machine's page, I switch to that machine and pick up its last conversation.
                </p>
                <p>
                  <strong>Fixed</strong> - I stay in this conversation wherever you go, so you can walk through other screens and keep asking about the
                  same thing.
                </p>
                <p className="od-ai-follow-note-fine">Either way I only switch when the panel goes up - never while you are typing.</p>
              </div>
            </div>
          </div>
          <div className="od-ai-mode-switch">
            <button
              type="button"
              className={`od-ai-mode${shownMode ? ` mode-${shownMode}` : ''}`}
              id="odAiMode"
              title="Change the agent mode"
              onClick={(e) => {
                e.stopPropagation()
                setModeMenu((v) => !v)
              }}
            >
              {modeLabel} <i className="fas fa-caret-down" aria-hidden="true"></i>
            </button>
            <div className="od-ai-mode-menu" id="odAiModeMenu" hidden={!modeMenu} onClick={(e) => e.stopPropagation()}>
              {modeList.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`od-ai-mode-pick${m.key === 'full' ? ' od-ai-mode-full' : ''}`}
                  data-mode={m.key}
                  onClick={() => switchMode(m.key)}
                >
                  <strong>{m.label}</strong>
                  <span>{m.say}</span>
                </button>
              ))}
              {modeList.some((m) => m.key === 'full') ? (
                <div className="od-ai-mode-warn">
                  Full Access lets the agent run <strong>any command</strong> on machines, with no catalogue. Every command is logged, on the device and
                  separately. Only a platform admin can turn it on.
                </div>
              ) : null}
            </div>
          </div>
          {/* Marirea sta langa coborare, ca la o fereastra: acolo o cauta omul,
              si e tot atat de mica cat sageata de langa ea. Un singur buton
              face si intoarce, si semnul lui spune in ce stare esti. */}
          {/* "Live commands" se ascunde / arata numai cand e marit - doar atunci
              exista. Poarta aceeasi clasa ca marirea: aceeasi marime, si se
              stinge odata cu ea pe telefon. */}
          {wide ? (
            <button
              type="button"
              className={`od-ai-wide od-ai-live-toggle${liveOn ? ' is-on' : ''}`}
              id="odAiLiveToggle"
              aria-pressed={liveOn}
              aria-label={liveOn ? 'Hide live commands' : 'Show live commands'}
              title={liveOn ? 'Hide live commands' : 'Show live commands'}
              onClick={toggleLive}
            >
              <i className="fas fa-terminal" aria-hidden="true"></i>
            </button>
          ) : null}
          <button
            type="button"
            className="od-ai-wide"
            id="odAiWide"
            aria-label={wide ? 'Shrink the panel back to its corner' : 'Grow the panel over the portal'}
            title={wide ? 'Shrink the panel back to its corner (Esc)' : 'Grow the panel over the portal'}
            aria-pressed={wide}
            onClick={() => {
              const next = !wide
              setWide(next)
              lsSet(WIDE_KEY, next ? '1' : '0')
            }}
          >
            <i className={`fas ${wide ? 'fa-compress-alt' : 'fa-expand-alt'}`} aria-hidden="true"></i>
          </button>
          <button type="button" className="od-ai-close" aria-label="Send it back down" title="Send it back down" onClick={() => setOpen(false)}>
            <i className="fas fa-chevron-down" aria-hidden="true"></i>
          </button>
        </div>

        <button type="button" className="od-ai-peek" id="odAiPeek" aria-expanded="false" aria-label="Show the panel options" title="Show the panel options">
          <i className="fas fa-chevron-down" aria-hidden="true"></i>
        </button>

        <div className="od-ai-toolbar">
          <div className="od-ai-scope-switch" id="odAiScopeSwitch">
            <button
              type="button"
              className={`od-ai-scope${scope.scope === 'all' ? ' is-active' : ''}`}
              data-scope="all"
              title="Ask about the whole fleet"
              onClick={() =>
                void pickScope({
                  scope: 'all',
                  device_id: 0,
                  conversation_id: 0,
                })
              }
            >
              All
            </button>
            <button
              type="button"
              className={`od-ai-scope${scope.scope === 'device' ? ' is-active' : ''}`}
              data-scope="device"
              title="Focus on one machine"
              onClick={(e) => {
                e.stopPropagation()
                setDeviceMenu(true)
                void searchDevices('')
              }}
            >
              Device
            </button>
          </div>
          <div className="od-ai-device" id="odAiDevice" hidden={scope.scope !== 'device' && !deviceMenu}>
            <button
              type="button"
              className="od-ai-device-pick"
              id="odAiDevicePick"
              onClick={(e) => {
                e.stopPropagation()
                if (deviceMenu) {
                  setDeviceMenu(false)
                  return
                }
                setDeviceMenu(true)
                void searchDevices('')
              }}
            >
              <span id="odAiDeviceName">{deviceName || 'Pick a machine'}</span> <i className="fas fa-caret-down" aria-hidden="true"></i>
            </button>
            <div className="od-ai-device-menu" id="odAiDeviceMenu" hidden={!deviceMenu} onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                className="od-ai-device-search"
                id="odAiDeviceSearch"
                placeholder="Search machines..."
                autoComplete="off"
                autoFocus
                value={deviceQuery}
                onChange={(e) => void searchDevices(e.target.value)}
              />
              <div className="od-ai-device-list" id="odAiDeviceList">
                {devices.length === 0 ? <div className="od-ai-device-empty">No machine matches.</div> : null}
                {devices.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className="od-ai-device-item"
                    data-id={d.id}
                    data-name={d.name}
                    onClick={() => {
                      setDeviceMenu(false)
                      void pickScope(
                        {
                          scope: 'device',
                          device_id: d.id,
                          conversation_id: 0,
                        },
                        d.name,
                      )
                    }}
                  >
                    <span>{d.name}</span>
                    <span className="od-ai-device-os">{d.os}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <span className="od-ai-toolbar-gap"></span>
          <button
            type="button"
            className="od-ai-modes-link"
            id="odAiHistory"
            title="Past conversations"
            style={{ background: 'none', border: 0, cursor: 'pointer' }}
            onClick={() => void openHistory()}
          >
            <i className="fas fa-history" aria-hidden="true"></i> History
          </button>
          <button
            type="button"
            className="od-ai-modes-link od-ai-new"
            id="odAiNew"
            title="New conversation (the old one stays in history)"
            style={{ background: 'none', border: 0, cursor: 'pointer' }}
            onClick={() => void newConversation()}
          >
            + New
          </button>
        </div>

        {/* Costul conversatiei si al lunii, in forma firmei; se reciteste dupa fiecare raspuns. */}
        <AdamSpend conversationId={scope.conversation_id} pulse={livePulse} open={open} />

        {/* Ridicat, cu mai multi Adam voie: bulele sunt filele rularilor. Cu
            limita 1 sertarul ramane cum era. */}
        {open && parallel > 1 ? (
          <AdamLives
            lives={liveBubbles}
            current={scope.conversation_id}
            inside
            onOpen={openLive}
            onClose={closeLive}
            onStop={stopLive}
            onBeside={besideOk ? (b) => void openBeside(b) : undefined}
          />
        ) : null}

        <div className="od-ai-running" id="odAiRunning" hidden={runs.length === 0}>
          <div className="od-ai-running-head">{live === 0 ? 'nothing running' : live === 1 ? '1 agent working' : `${live} agents working`}</div>
          {runs.map((run) => (
            <div key={run.conversation_id} className="od-ai-running-row">
              <button type="button" className="od-ai-running-open" onClick={() => void openConversation(run.conversation_id)}>
                {run.state === 'running' ? (
                  <span className="od-ai-spinner" aria-hidden="true"></span>
                ) : (
                  <span className="od-ai-running-dead" aria-hidden="true">
                    !
                  </span>
                )}
                <span className="od-ai-running-where">{run.where}</span>
                <span className="od-ai-running-q">{run.question}</span>
                <span className="od-ai-running-time">{elapsed(run.seconds)}</span>
              </button>
              <button
                type="button"
                className="od-ai-running-act"
                data-id={run.conversation_id}
                data-act={run.state === 'running' ? 'stop' : 'dismiss'}
                onClick={() => void (run.state === 'running' ? aiCancel(scope, run.conversation_id) : aiDismissRun(run.conversation_id)).then(refreshRunning)}
              >
                {run.state === 'running' ? 'Stop' : 'Dismiss'}
              </button>
            </div>
          ))}
        </div>

        {wide ? (
          <div ref={split} className={`od-ai-split${dragging ? ' is-dragging' : ''}`} id="odAiSplit">
            <div className="od-ai-split-main">{chatView}</div>
            {/* Montat numai cat panoul e sus si aratat: coborat sau ascuns, nu mai citeste nimic. */}
            {liveOn && open ? (
              <>
                <div
                  className="od-ai-split-bar"
                  id="odAiSplitBar"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize live commands"
                  aria-valuemin={LIVE_MIN}
                  aria-valuemax={LIVE_MAX}
                  aria-valuenow={liveW}
                  tabIndex={0}
                  onMouseDown={startDrag}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowLeft') setLive(liveW + 2, true)
                    else if (e.key === 'ArrowRight') setLive(liveW - 2, true)
                  }}
                />
                <LiveCommands scope={scope} steps={liveSteps} busy={busy} pulse={livePulse} style={{ flexBasis: `${liveW}%` }} />
              </>
            ) : null}
          </div>
        ) : (
          chatView
        )}

        <div className="od-ai-panel" id="odAiPanel" hidden={panel === null}>
          <div className="od-ai-panel-head">
            <strong id="odAiPanelTitle">{panel?.title ?? ''}</strong>
            <button type="button" className="od-ai-panel-close" aria-label="Close" onClick={() => setPanel(null)}>
              &times;
            </button>
          </div>
          <div className="od-ai-panel-body" id="odAiPanelBody">
            {panel?.kind === 'history' ? (
              convs.length === 0 ? (
                <p className="text-muted">No past conversations yet.</p>
              ) : (
                (() => {
                  /* Ale omului sus; raspunsurile automate la alarme (system = 1)
                     intr-o sectiune a lor, dedesubt, restransa - sute pe zi, nu
                     mai au voie sa le acopere pe ale omului. */
                  const item = (c: AiConversation) => (
                    <button key={c.id} type="button" className="od-ai-history-item" data-id={c.id} onClick={() => void openConversation(c.id)}>
                      <span className="od-ai-history-line">
                        <span className="od-ai-history-title">{c.title}</span>
                        <span className="od-ai-history-when">{String(c.updated_at ?? c.started ?? '')}</span>
                      </span>
                      <span className="od-ai-history-meta">
                        {String(c.scope || 'whole fleet')} · {String(c.count ?? 0)} messages
                      </span>
                    </button>
                  )
                  const own = convs.filter((c) => Number(c.system ?? 0) !== 1)
                  const alarms = convs.filter((c) => Number(c.system ?? 0) === 1)
                  return (
                    <>
                      <div className="od-ai-history-own" id="odAiHistoryOwn">
                        {own.length === 0 ? <p className="text-muted">No past conversations yet.</p> : own.map(item)}
                      </div>
                      {alarms.length > 0 ? (
                        <details className="od-ai-history-system" id="odAiHistorySystem">
                          <summary className="od-ai-history-system-head">Alarm responses ({alarms.length})</summary>
                          {alarms.map(item)}
                        </details>
                      ) : null}
                    </>
                  )
                })()
              )
            ) : null}
            {panel?.kind === 'commands' ? (
              commands.length === 0 ? (
                <p className="text-muted">No commands from this conversation.</p>
              ) : (
                commands.map((c) => (
                  <div key={c.id} className="od-ai-cmd-card">
                    <div className="od-ai-cmd-head">
                      <code>{c.command}</code> · {c.device} · {c.at} · {c.state}
                    </div>
                    <pre className="od-ai-cmd-output">{c.output}</pre>
                  </div>
                ))
              )
            ) : null}
          </div>
        </div>

        <div className="od-ai-gate" id="odAiGate" hidden={gate === null}>
          <div className="od-ai-gate-box">
            <div className="od-ai-gate-head">Turning on Full Access</div>
            <ul className="od-ai-gate-risks">
              <li>
                The agent can run <strong>ANY command</strong> on your machines &mdash; not only the ones in the approved catalogue.
              </li>
              <li>
                It applies to <strong>every machine of this company</strong>, not just the one you are looking at.
              </li>
              <li>
                Commands run <strong>immediately</strong>. Nothing waits for a second signature.
              </li>
              <li>
                The mode <strong>stays on</strong> until somebody switches it back &mdash; including after you log out.
              </li>
              <li>Every command is logged, on the device and in Direct Commands. Nothing is hidden, but nothing is undone either.</li>
            </ul>
            <p className="od-ai-gate-sent" id="odAiGateSent">
              {gate?.sentTo ? `We sent a code to ${gate.sentTo}. It is good for five minutes.` : ''}
            </p>
            <div className="od-ai-gate-agree">
              <SettingsCheck id="odAiGateAgree" checked={gateAgree} onChange={setGateAgree} label="I have read the above and I agree" />
            </div>
            <input
              type="text"
              className="od-ai-gate-code"
              id="odAiGateCode"
              maxLength={8}
              hidden={!gate?.needCode}
              autoComplete="off"
              spellCheck={false}
              placeholder="8-character code"
              value={gateCode}
              onChange={(e) => setGateCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && gateReady && !gateBusy) void gateGo()
              }}
            />
            <p className="od-ai-gate-error" id="odAiGateError" hidden={!gate?.error}>
              {gate?.error ?? ''}
            </p>
            <div className="od-ai-gate-actions">
              <button type="button" className="od-ai-gate-cancel" id="odAiGateCancel" onClick={() => setGate(null)}>
                Cancel
              </button>
              <button type="button" className="od-ai-gate-go" id="odAiGateGo" disabled={!gateReady || gateBusy} onClick={() => void gateGo()}>
                Turn on Full Access
              </button>
            </div>
          </div>
        </div>

        <div className="od-ai-compose">
          <div className="od-ai-compose-field">
            <textarea
              id="odAiInput"
              rows={2}
              placeholder={
                atLimit && !busy
                  ? parallel > 1
                    ? `${working} Adam runs are working - your limit is ${parallel}. Wait for one to finish.`
                    : 'Adam is working on another conversation. Wait for it to finish.'
                  : 'Ask the agent...'
              }
              title="Enter sends · Shift+Enter new line"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
            />
            <button type="button" id="odAiSend" className="od-ai-send" title="Send" aria-label="Send" disabled={busy || atLimit} onClick={() => void send()}>
              <i className="fas fa-paper-plane" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="od-ai-mcp">
          <a
            href="#"
            className="od-ai-mcp-toggle"
            onClick={(e) => {
              e.preventDefault()
              setMcpOpen((v) => !v)
            }}
          >
            MCP &#9662;
          </a>
          <div className="od-ai-acts">
            <button type="button" className="od-ai-act" data-act="save" title="Save this conversation as a .txt file" onClick={() => void act('save')}>
              <i className="fas fa-download" aria-hidden="true"></i>
            </button>
            <button type="button" className="od-ai-act" data-act="copy" title="Copy the whole conversation to the clipboard" onClick={() => void act('copy')}>
              <i className="fas fa-copy" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="od-ai-act"
              data-act="ticket"
              title="Open an Easeedesk ticket and start a new conversation"
              onClick={() => void act('ticket')}
            >
              <i className="fas fa-ticket-alt" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="od-ai-act"
              data-act="schedule"
              title="Schedule this question: the agent answers it on a schedule and mails the report"
              onClick={() => void act('schedule')}
            >
              <i className="fas fa-clock" aria-hidden="true"></i>
            </button>
          </div>
          <span className="od-ai-gap" aria-hidden="true"></span>
          <a href="/app/change" className="od-ai-session-link" id="odAiChangeLink" hidden={made.changes === 0} title="Change proposals from this conversation">
            <i className="fas fa-code-branch" aria-hidden="true"></i> Changes{' '}
            <span className="od-ai-cmd-badge" id="odAiChangeBadge">
              {made.changes}
            </span>
          </a>
          <a
            href="/app/assistant/activity"
            className="od-ai-session-link od-ai-activity-link"
            id="odAiCmdLink"
            hidden={made.commands === 0}
            title="Commands run from this conversation"
            onClick={(e) => {
              e.preventDefault()
              void openCommands()
            }}
          >
            <i className="fas fa-terminal" aria-hidden="true"></i> Direct Commands{' '}
            <span className="od-ai-cmd-badge" id="odAiCmdBadge">
              {made.commands}
            </span>
          </a>
          <div className="od-ai-mcp-body" style={{ display: mcpOpen ? 'block' : 'none' }}>
            <p>
              The same agent can be used from <strong>Claude Desktop</strong> or <strong>Claude Code</strong>, on your own subscription: the{' '}
              <code>od-mcp</code> binary (ask your administrator for it) goes into the client's MCP configuration:
            </p>
            <pre>{`{
  "mcpServers": {
    "optimumdesk": {
      "command": "/path/to/od-mcp",
      "env": {
        "OD_MCP_DSN": "user:password@tcp(host:3306)/database"
      }
    }
  }
}`}</pre>
            <p>
              In Claude Desktop: Settings &rarr; Developer &rarr; Edit Config. In Claude Code: the project's <code>.mcp.json</code> file. The database
              user comes from your administrator - it can only read, plus write proposals. The rules are the same wherever you come in from: the modes
              switched at <a href="/app/change">/change</a> apply there too.
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}

/**
 * Firul unei conversatii: bulele, pasii, asteptarea cu Stop. Acelasi in sertar
 * si in panoul alaturat - numai datele difera.
 */
function AdamThread({
  bubbles,
  wait,
  since,
  wide,
  onStop,
  chatRef,
  id,
}: {
  bubbles: Bubble[]
  wait: Wait | null
  since: number
  wide: boolean
  onStop(): void
  chatRef?: RefObject<HTMLDivElement | null>
  id?: string
}) {
  const own = useRef<HTMLDivElement>(null)
  const ref = chatRef ?? own
  useEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [bubbles, wait, ref])
  return (
    <div className="od-ai-chat" id={id} ref={ref}>
      {bubbles.length === 0 ? (
        <div className="od-ai-hello">
          I am your infrastructure agent. Ask me about the fleet, the log, the sessions - or put me to work, according to the mode I am switched to:{' '}
          <em>Investigation</em> only investigates (default), <em>Change</em> prepares proposals you approve, <em>Incident</em> fixes right away.
        </div>
      ) : null}
      {bubbles.map((b) => {
        const msg =
          b.who === 'ai' ? (
            <div className="od-ai-msg from-ai" dangerouslySetInnerHTML={{ __html: renderRich(b.text) }} />
          ) : (
            <div className="od-ai-msg from-user">{b.text}</div>
          )
        const steps = b.steps.length > 0 ? <Steps list={b.steps} /> : null
        /* Panoul mic ramane cum era: bula si pasii, unul sub altul. */
        if (!wide)
          return (
            <div key={b.key}>
              {msg}
              {steps}
            </div>
          )
        /* Marit, asezare de chat: omul la dreapta, Adam la stanga cu capul lui
           langa bula, ora sub ea. Se aliniaza RANDUL - bula dintr-un <div>
           simplu nu ajungea niciodata la dreapta, fiindca `align-self` nu trece
           prin el. */
        const time = msgTime(b.at)
        return (
          <div key={b.key} className={`od-ai-row ${b.who === 'ai' ? 'from-ai' : 'from-user'}`}>
            {b.who === 'ai' ? (
              <span className="od-ai-row-avatar" aria-hidden="true">
                <Adam size={24} />
              </span>
            ) : null}
            <div className="od-ai-row-body">
              {msg}
              {steps}
              {time !== '' ? <span className="od-ai-row-time">{time}</span> : null}
            </div>
          </div>
        )
      })}
      {wait
        ? (() => {
            const waiting = (
              <div className="od-ai-msg from-ai is-waiting">
                <span className="od-ai-wait-what">{wait.what}</span> <span className="od-ai-wait-time">{elapsed(since)}</span>
                <button type="button" className="od-ai-wait-stop" title="Stop this run" aria-label="Stop this run" onClick={onStop}>
                  <span className="od-ai-wait-stop-ring" aria-hidden="true"></span>
                  <span className="od-ai-wait-stop-label" aria-hidden="true"></span>
                </button>
                <span className="od-ai-wait-note" hidden={wait.note === ''}>
                  {wait.note}
                </span>
              </div>
            )
            if (!wide) return waiting
            return (
              <div className="od-ai-row from-ai">
                <span className="od-ai-row-avatar" aria-hidden="true">
                  <Adam size={24} />
                </span>
                <div className="od-ai-row-body">{waiting}</div>
              </div>
            )
          })()
        : null}
    </div>
  )
}

/** Pasii agentului: un rand, si lista dedesubt daca vrei sa o vezi. */
function Steps({ list }: { list: AiStep[] }) {
  const [open, setOpen] = useState(false)
  const n = list.length
  return (
    <div className="od-ai-steps">
      <button type="button" className="od-ai-steps-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i className="fas fa-cog" aria-hidden="true"></i> {n} {n === 1 ? 'tool used' : 'tools used'}{' '}
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} od-ai-steps-caret`} aria-hidden="true"></i>
      </button>
      <div className="od-ai-steps-body" hidden={!open}>
        {list.map((step, i) => (
          <div key={i}>
            <code>{step.tool}</code>
            {step.args && step.args !== '{}' ? <span className="od-ai-step-args"> {step.args}</span> : null}
          </div>
        ))}
      </div>
    </div>
  )
}
